/**
 * Helpers puros para o param `product_key` nas URLs dos endpoints.
 *
 * Fazem parte da recuperação silenciosa do 403 de product_key (issue #1841):
 * o wrapper do baseQuery usa `extractProductKey` para descobrir a chave usada
 * na request que falhou, e `replaceProductKey` para reescrever a URL antes do
 * retry. Quando não há chave no store, `getProductKey` devolve a sentinela
 * `__missing__<produto>` — o produto viaja dentro do próprio param e
 * `missingKeyProduct` o recupera sem precisar de mapa rota→produto.
 *
 * Módulo sem dependências (zero imports) — seguro em qualquer grafo de import.
 */

export const MISSING_KEY_PREFIX = '__missing__';

const PRODUCT_KEY_PARAM_RE = /([?&]product_key=)([^&]*)/;

type UrlArgs = string | { url: string };

const argsUrl = (args: UrlArgs): string => (typeof args === 'string' ? args : args.url);

/** Chave usada na request; null quando a URL nem tem o param product_key. */
export function extractProductKey(args: UrlArgs): string | null {
  const match = argsUrl(args).match(PRODUCT_KEY_PARAM_RE);
  return match ? decodeURIComponent(match[2]) : null;
}

/** Produto embutido na sentinela `__missing__<produto>`; null se não for sentinela. */
export function missingKeyProduct(key: string | null): string | null {
  if (key && key.startsWith(MISSING_KEY_PREFIX)) {
    return key.slice(MISSING_KEY_PREFIX.length);
  }
  return null;
}

const rewriteUrl = (url: string, freshKey: string) => url.replace(
  PRODUCT_KEY_PARAM_RE,
  (_, prefix) => `${prefix}${encodeURIComponent(freshKey)}`,
);

/**
 * Reescreve o param product_key preservando o resto da URL (string ou {url}).
 *
 * Sobrecargas em vez de um generico unico: `T extends UrlArgs` aceitaria `string`,
 * e o TS proibe espalhar (`...args`) um tipo que pode ser string (TS2698).
 */
export function replaceProductKey(args: string, freshKey: string): string;
export function replaceProductKey<T extends { url: string }>(args: T, freshKey: string): T;
export function replaceProductKey(args: UrlArgs, freshKey: string): UrlArgs {
  if (typeof args === 'string') {
    return rewriteUrl(args, freshKey);
  }
  return { ...args, url: rewriteUrl(args.url, freshKey) };
}
